import type { HairlineProps } from "@lucasmarkes/hairline/react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
} from "react";

import globe from "./engines/globe.js";
import mark from "./engines/mark.js";
import shelf from "./engines/shelf.js";
import type { HairlineFigure } from "./kernel.js";
import { mountFigure, type MountedFigure } from "./mount";

type HairlineComponent = ForwardRefExoticComponent<HairlineProps & RefAttributes<HTMLDivElement>>;

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * A component for a figure of our own, made the way `@lucasmarkes/hairline/react`
 * makes the package's: one empty `<div>`, the figure mounted on it once in a
 * layout effect, later options sent as `update`, and `onRead` read through a ref.
 */
function make(name: string, figure: HairlineFigure): HairlineComponent {
  const Component = forwardRef<HTMLDivElement, HairlineProps>(function Hairline(props, ref) {
    const { intensity, theme, label, onRead, play, style, ...attrs } = props;
    const named = label ?? props["aria-label"];

    const el = useRef<HTMLDivElement | null>(null);
    const handle = useRef<MountedFigure | null>(null);
    const read = useRef(onRead);
    const set = useCallback(
      (node: HTMLDivElement | null) => {
        el.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    useIsoLayoutEffect(() => {
      read.current = onRead;
    });

    useIsoLayoutEffect(() => {
      const f = mountFigure(figure, el.current!, {
        intensity,
        theme,
        label: named,
        play,
        onRead: (text) => read.current?.(text),
      });
      handle.current = f;
      return () => {
        f.destroy();
        handle.current = null;
      };
      // mounts once; options reach the figure through the effect below
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* every key is sent, so a prop that was removed goes back to its default */
    useIsoLayoutEffect(() => {
      handle.current?.update({ intensity, theme, label: named, play });
    }, [intensity, theme, named, play]);

    return <div {...attrs} ref={set} style={{ aspectRatio: "5 / 4", ...style }} />;
  });
  Component.displayName = name;
  return Component;
}

/** A desk globe on its meridian ring: a flick spins it, friction bleeds the spin, and it catches on a stop. `intensity` lets it coast longer. */
export const Globe = make("Globe", globe);
/** A rulebook shelf whose bays hold no binders: the pointer picks a bay. */
export const Shelf = make("Shelf", shelf);
/** Amoreg's mark, built as a solid: its leg lands in place as the pointer nears. */
export const AmoregMark = make("AmoregMark", mark);
